import { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation';
import {
	type Agent,
	type AgentDetails,
	ApiError,
	type AssistanceServiceInjectedParams,
	createHookWithAssistanceService,
	isRateLimitedError,
	performGqlFetch,
	useMutation,
	useQuery,
} from '@atlassian/conversation-assistant-service';

import { type BrowseCategory } from '../../common/types';
import { getSuggestedOotbAgents } from '../../utils/agents';
import { useDeleteAgent } from '../agent-delete';
import { useAgentFavourites } from '../agent-favourite';
import { getOverridenAgents, useAgentOverrider } from '../agent-overrider';

import { isHipaaError } from './constants';
import messages from './messages';

type AgentsAggResponse = {
	agents: Agent[];
	pageInfo: PageInfo;
};

const fetchAgg = async <TVariables = Record<string, unknown>,>({
	gqlQuery,
	variables = {} as TVariables,
	abortController,
}: {
	gqlQuery: string;
	variables: TVariables;
	abortController?: AbortController;
}) => {
	const response = await performGqlFetch({
		gqlQuery,
		variables,
		abortController,
	});

	return transformAgentsGqlResponse(response);
};

const transformAgentsGqlResponse = (response: any): AgentsAggResponse => {
	try {
		const { edges, pageInfo } = response?.data?.agentStudio_getAgents;

		const agents =
			edges?.map((edge: any) => {
				return {
					id: edge.node.id,
					name: edge.node.name,
					description: edge.node.description,
					user_defined_conversation_starters: edge.node.conversationStarters,
					creator: edge.node.creator.name,
					favourite: edge.node.isFavourite,
					// TODO currently missing from the response; defaulting to SYSTEM
					// ref: https://atlassian.slack.com/archives/C08UX4QJB0B/p1750139655770729?thread_ts=1750139096.901889&cid=C08UX4QJB0B
					creator_type: 'SYSTEM',
				} as Partial<Agent>;
			}) ?? [];

		return { agents, pageInfo };
	} catch (error) {
		return {
			agents: [],
			pageInfo: {
				hasNextPage: false,
				hasPreviousPage: false,
				startCursor: undefined,
				endCursor: undefined,
			},
		};
	}
};

type AggParams = {
	cloudId: string;
	// Number of agents to fetch
	count?: number;
	// Cursor token for pagination, e.g "from this point"
	after?: string;
	// Filtering
	input?: {
		onlyFavouriteAgents?: boolean;
		onlyMyAgents?: boolean;
		// This will search agent name, and also description on the BE
		name?: string;
	};
	abortController?: AbortController;
};

const fetchPaginatedAgents = async ({
	cloudId,
	count = 10,
	after,
	input,
	abortController,
}: AggParams): Promise<AgentsAggResponse> => {
	const gqlQuery = `
	query agentStudioGetAgents($cloudId: String!, $first: Int, $input: AgentStudioAgentQueryInput, $after: String) {
		agentStudio_getAgents(
			cloudId: $cloudId
			first: $first
			input: $input
			after: $after
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					id
					name
					description
					conversationStarters
					creator {
						name
					}
				}
			}
			pageInfo {
				startCursor
				endCursor
				hasNextPage
				hasPreviousPage
			}
		}
	}`;

	return fetchAgg({
		gqlQuery,
		variables: { cloudId, first: count, after, input },
		abortController,
	});
};

type CategorisedAgents = {
	public: Agent[];
	private: Agent[];
	system: Agent[];
};

const categoriseAgents = (agents: Agent[]): CategorisedAgents =>
	agents.reduce(
		(categorisedAgents: CategorisedAgents, agent: Agent) => {
			if (agent.creator_type === 'SYSTEM') {
				categorisedAgents.system.push(agent);
			} else if (agent.visibility === 'PUBLIC') {
				categorisedAgents.public.push(agent);
			} else if (agent.visibility === 'PRIVATE') {
				categorisedAgents.private.push(agent);
			} else if (!agent.visibility && agent.creator_type === 'CUSTOMER') {
				categorisedAgents.private.push(agent);
			}

			return categorisedAgents;
		},
		{
			public: [],
			private: [],
			system: [],
		},
	);

const agentsStore = createStore({
	initialState: {
		availableAgents: [] as Agent[],
		categorisedAgents: {
			private: [],
			public: [],
			system: [],
		} as CategorisedAgents,
		agentsInitialized: false,
		agentsError: undefined as ApiError | undefined,
	},
	name: 'AgentsStore',
	actions: {
		setAgents:
			(availableAgents: Agent[]) =>
			({ setState }) => {
				setState({
					availableAgents,
					categorisedAgents: categoriseAgents(availableAgents),
				});
			},

		setAgentsInitialized:
			() =>
			({ setState }) => {
				setState({
					agentsInitialized: true,
				});
			},

		setAgentsError:
			(agentsError: ApiError) =>
			({ setState }) => {
				setState({ agentsError });
			},
	},
});

const useAgentsStore = createHook(agentsStore);

type UseAgentsListProps = {
	filter?: 'favourites';
} & AssistanceServiceInjectedParams;

type UseAgentsControllerProps = {
	onError?: (error: ApiError) => void;
} & AssistanceServiceInjectedParams;

// RAGE-60 - Older non-paginated controller, to be removed later
export const useAgentsControllerInternal = createHookWithAssistanceService(
	({
		onError,
		assistanceServiceInjectedParams: {
			assistanceService,
			experienceTracker,
			experienceId,
			product,
			siteId,
			touchpointSource,
		},
	}: UseAgentsControllerProps) => {
		const { isRovoEnabled } = useRovoEntitlement();

		const { agentIdFiltersOverride, agentDefaultIdOverride } = useAgentOverrider();
		const [
			{ availableAgents, categorisedAgents, agentsInitialized, agentsError },
			{ setAgents, setAgentsInitialized, setAgentsError },
		] = useAgentsStore();
		const { formatMessage } = useIntl();

		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
		});

		const favouritedAgents = availableAgents.filter((agent) => agent.favourite);
		const defaultAgent = useMemo(
			() => availableAgents.find((agent) => agent.is_default),
			[availableAgents],
		);
		const nonDefaultAgents = useMemo(
			() => availableAgents.filter((agent) => !agent.is_default),
			[availableAgents],
		);
		const nonDefaultFavouriteAgents = useMemo(
			() => nonDefaultAgents.filter((agent) => !agent.favourite),
			[nonDefaultAgents],
		);

		const fetchAndStoreAvailableAgents = useQuery({
			queryKey: ['fetchAgents'],
			queryFn: async () => {
				try {
					experienceTracker.start({
						id: ExperienceName.FETCH_AGENTS,
						name: ExperienceName.FETCH_AGENTS,
					});

					const agents = await assistanceService.getAgents();

					const transformedAgents = agents.map((agent) => {
						// Rename default agent to Chat, which is displayed in the UI
						if (agent.is_default) {
							agent.name = formatMessage(messages.defaultAgentName);
							agent.description = formatMessage(messages.defaultAgentDescription);
						}

						return agent;
					});

					const overriddenAgents = getOverridenAgents(
						transformedAgents,
						agentIdFiltersOverride,
						agentDefaultIdOverride,
					);

					setAgents(overriddenAgents);

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENTS,
					});

					setAgentsInitialized();
				} catch (error: any) {
					//Filter out HIPAA errors from experience tracker https://product-fabric.atlassian.net/browse/ROVOFE-543
					if (isHipaaError(error)) {
						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS,
						});
						return;
					}

					if (isRateLimitedError(error)) {
						setAgentsError(error);
					}

					experienceTracker.fail({ name: ExperienceName.FETCH_AGENTS, error });
					throw error;
				}
			},
			onError,
			enabled: isRovoEnabled,
			refetchOnWindowFocus: fg('rovo_chat_disable_agent_fetch_on_visibility') ? false : true,
		});

		const { deleteAgent } = useDeleteAgent({
			onSuccess: () => {
				fetchAndStoreAvailableAgents.refetch();
			},
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
			onInit: (id) => {
				setAgents(availableAgents.filter((agent) => agent.id !== id));
			},
			onError: () => {
				setAgents(availableAgents);
			},
		});

		const setFavouriteAgent = useCallback(
			async (id: string, isFavourite: boolean) => {
				const updatedAgent = availableAgents.find((a) => a.id === id);
				const setUpdatedAgent = (shouldBeFavourite: boolean) => {
					if (updatedAgent) {
						updatedAgent.favourite = shouldBeFavourite;
						setAgents(
							availableAgents.map((a) => {
								// If the current agent's id matches the new agent's id, return the new agent.
								// Otherwise, return the current agent.
								return a.id === updatedAgent.id ? updatedAgent : a;
							}),
						);
					}
				};

				//Optimistically update favourite value before we mutate
				setUpdatedAgent(isFavourite);

				//Pass callback to roll back optimistic update if mutation fails
				const errorCallback = {
					onError: () => {
						setUpdatedAgent(!isFavourite);
					},
				};
				if (isFavourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[availableAgents, setAgents, favouriteAgent, unfavouriteAgent],
		);
		/** This updates the agent on the client side only
		 * Useful for updating the agent after a successful mutation
		 */
		const updateAgentLocally = useCallback(
			(agentId: string, updatedAgent: Partial<Agent>) => {
				const updatedAgents = availableAgents.map((agent) => {
					if (agent.id === agentId) {
						return {
							...agent,
							...updatedAgent,
						};
					}
					return agent;
				});

				setAgents(updatedAgents);
			},
			[availableAgents, setAgents],
		);

		return {
			availableAgents,
			favouritedAgents,
			categorisedAgents,
			fetchAndStoreAvailableAgents,
			deleteAgent,
			setFavouriteAgent,
			defaultAgent,
			agentsInitialized,
			nonDefaultAgents,
			nonDefaultFavouriteAgents,
			updateAgentLocally,
			agentsError,
		};
	},
);

type StoreAgentCategories = Record<
	BrowseCategory,
	{
		agentIds: string[];
		pageInfo: PageInfo;
		abortController: AbortController;
	}
>;

type PageInfo = {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	startCursor: string | undefined;
	endCursor: string | undefined;
};
const DEFAULT_PAGE_INFO: PageInfo = {
	hasNextPage: false,
	hasPreviousPage: false,
	startCursor: undefined,
	endCursor: undefined,
};

/*
TODO: RAGE-60 Notes 
- `agentsInitialized` was only used in one callsite `platform/packages/ai-mate/conversation-assistant/src/ui/chat-history/empty-state/default-agent-empty-state/index.tsx` 
But it's not clear if it's needed there with the paginated updated. Need to check.
*/
const agentsPaginatedStore = createStore({
	initialState: {
		/* 
		Hash map which is the source of truth for agent data
		agent categories simply hold the agent ids to perform a lookup
		*/
		agentsMap: {} as Record<string, Agent>,
		loadingCategories: new Set<BrowseCategory>(),
		agentCategories: {
			['all-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['starred-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['my-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['development']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['templates']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
		} as StoreAgentCategories,
		agentsError: undefined as ApiError | undefined,
	},
	name: 'AgentsPaginatedStore',
	actions: {
		setAgentsByCategory:
			({
				agentsList,
				pageInfo,
				category = 'all-agents',
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				updatedAgentCategories[category].pageInfo = pageInfo;

				if (!agentsList.length) {
					updatedAgentCategories[category].agentIds = [];
				} else {
					agentsList.forEach((agent) => {
						updatedAgentsMap[agent.id] = agent;
						updatedAgentCategories[category].agentIds.push(agent.id);
					});
				}

				setState({
					agentsMap: updatedAgentsMap,
					agentCategories: updatedAgentCategories,
				});
			},

		setAgentsError:
			(agentsError: ApiError) =>
			({ setState }) => {
				setState({ agentsError });
			},

		removeAgent:
			(id: string) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const catsImpacted: Set<BrowseCategory> = new Set();
				let agentToReturn: Agent | undefined;

				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				if (agentsMap[id]) {
					agentToReturn = agentsMap[id];
					delete updatedAgentsMap[id];
				}

				(Object.keys(agentCategories) as BrowseCategory[]).forEach((category) => {
					if (updatedAgentCategories[category]?.agentIds.includes(id)) {
						updatedAgentCategories[category].agentIds = updatedAgentCategories[
							category
						].agentIds.filter((agentId: string) => agentId !== id);
						catsImpacted.add(category);
					}
				});

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });

				return {
					agent: agentToReturn,
					categories: Array.from(catsImpacted),
				};
			},

		addAgent:
			(agent: Agent, categories: BrowseCategory[] = ['all-agents']) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				categories.forEach((category) => {
					updatedAgentCategories?.[category]?.agentIds.push(agent.id);
				});

				updatedAgentsMap[agent.id] = agent;

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });
			},

		updateAgent:
			(id: string, data: Partial<Agent>) =>
			({ setState, getState }) => {
				const { agentsMap } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const prevData = agentsMap[id] ?? {};
				const updatedData = { ...prevData, ...data };

				updatedAgentsMap[id] = updatedData;

				setState({ agentsMap: updatedAgentsMap });

				return updatedData;
			},

		setCategoryLoading:
			(category: BrowseCategory, loading: boolean) =>
			({ setState, getState }) => {
				const { loadingCategories } = getState();
				const updatedLoadingCategories = new Set(loadingCategories);

				if (loading) {
					updatedLoadingCategories.add(category);
				} else {
					updatedLoadingCategories.delete(category);
				}

				setState({ loadingCategories: updatedLoadingCategories });
			},
	},
});

const useAgentsPaginatedStore = createHook(agentsPaginatedStore);
const useAgentsPaginatedStoreActions = createActionsHook(agentsPaginatedStore);

// RAGE-60 - Only handles favourite actions and optimistic update layer, maybe it can handle more?
export const useAgentsPaginatedOptimisticUpdates = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: UseAgentsControllerProps) => {
		const removedAgentCache = useRef<{
			categories: BrowseCategory[];
			agent: Agent | undefined;
		}>({
			categories: [],
			agent: undefined,
		});

		const { removeAgent, addAgent, updateAgent } = useAgentsPaginatedStoreActions();
		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		// Note: Experience Tracker events implemented in useDeleteAgent
		const { deleteAgent } = useDeleteAgent({
			assistanceServiceParams: assistanceServiceInjectedParams,
			onInit: (id) => {
				const { categories, agent } = removeAgent(id);
				removedAgentCache.current = { categories, agent };
			},
			onError: () => {
				if (removedAgentCache.current.agent) {
					addAgent(removedAgentCache.current.agent, removedAgentCache.current.categories);
				}
			},
		});

		// Note: Experience Tracker events implemented in useAgentFavourites
		const setFavouriteAgent = useCallback(
			async (id: string, favourite: boolean) => {
				// Optimistic update
				updateAgent(id, { favourite });

				const errorCallback = {
					onError: () => {
						// Reverse optimistic update
						updateAgent(id, { favourite: !favourite });
					},
				};

				if (favourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[favouriteAgent, unfavouriteAgent, updateAgent],
		);

		return {
			deleteAgent,
			setFavouriteAgent,
		};
	},
);

const getLoaderAgent = (agentId: string): Agent => ({
	id: agentId,
	named_id: agentId,
	name: '',
	description: '',
	user_defined_conversation_starters: [],
	available_plugins: {},
	actor_type: 'AGENT',
	creator_type: 'SYSTEM',
	is_default: false,
	visibility: 'PRIVATE',
	favourite: false,
	deactivated: false,
	requires_hydration: true,
});

// TODO: RAGE-60 - Check how the nonDefaultAgents are used in `useBrowseAgentsUI`
export const usePaginatedAgents = createHookWithAssistanceService(
	({
		onError,
		assistanceServiceInjectedParams: {
			assistanceService,
			experienceTracker,
			experienceId,
			product,
			siteId,
			touchpointSource,
		},
	}: UseAgentsControllerProps) => {
		const [
			{ agentsError, agentsMap, agentCategories, loadingCategories },
			{ updateAgent, setAgentsByCategory, setCategoryLoading },
		] = useAgentsPaginatedStore();

		// TODO: RAGE-60
		// - Account for overrider logic — By Promise.all() on get agent details with the ids array ref: https://atlassian.slack.com/archives/C08UX4QJB0B/p1750819232262579
		// TODO: Handle genuine error states
		const getAgents = async ({
			category = 'all-agents',
			after,
			query,
		}: {
			category?: BrowseCategory;
			after?: string;
			query?: string;
		}): Promise<string[]> => {
			const experienceTrackerAttr = {
				category,
				product,
				siteId,
				experienceId,
				touchpointSource,
			};
			experienceTracker.start({
				name: ExperienceName.FETCH_AGENTS_PAGINATED,
				id: ExperienceName.FETCH_AGENTS_PAGINATED,
				attributes: experienceTrackerAttr,
			});

			const dataBeforeFetch = agentCategories[category];
			let queryInput: {
				onlyCodingAgents?: boolean;
				onlyTemplateAgents?: boolean;
				onlyFavouriteAgents?: boolean;
				onlyMyAgents?: boolean;
				name?: string;
			} = {};

			if (['starred-agents', 'my-agents', 'development', 'templates'].includes(category)) {
				queryInput.onlyFavouriteAgents = category === 'starred-agents';
				queryInput.onlyMyAgents = category === 'my-agents';
				queryInput.onlyCodingAgents = category === 'development';
				queryInput.onlyTemplateAgents = category === 'templates';
			}

			if (query) {
				queryInput.name = query;
			}

			// No after means we're requesting the first page so the category data is reset
			if (!after) {
				setAgentsByCategory({
					category,
					agentsList: [],
					pageInfo: DEFAULT_PAGE_INFO,
				});
			}

			setCategoryLoading(category, true);

			const resetData = (): string[] => {
				setAgentsByCategory({
					category,
					agentsList: dataBeforeFetch.agentIds?.map((agentId) => agentsMap[agentId]),
					pageInfo: dataBeforeFetch.pageInfo,
				});
				return dataBeforeFetch.agentIds;
			};

			try {
				const response = await fetchPaginatedAgents({
					after,
					cloudId: siteId,
					count: 10,
					input: Object.keys(queryInput).length > 0 ? queryInput : undefined,
					abortController: agentCategories[category].abortController,
				});

				setCategoryLoading(category, false);

				if (agentCategories[category].abortController.signal?.aborted) {
					experienceTracker.abort({
						reason: 'Aborted',
						name: ExperienceName.FETCH_AGENTS_PAGINATED,
						attributes: experienceTrackerAttr,
					});
					return resetData();
				}

				if (response) {
					setAgentsByCategory({
						category,
						agentsList: response.agents,
						pageInfo: response.pageInfo,
					});

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENTS_PAGINATED,
						attributes: experienceTrackerAttr,
					});
				}

				return response?.agents.map((agent) => agent.id);
			} catch (error) {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENTS_PAGINATED,
					error: error as Error,
					attributes: experienceTrackerAttr,
				});
				setCategoryLoading(category, false);
				return resetData();
			}
		};

		const getAgentData = useCallback(
			(agentId: string) => {
				return agentsMap[agentId] ?? getLoaderAgent(agentId);
			},
			[agentsMap],
		);

		const hydrateAgentData = useCallback(
			async (agentId: string) => {
				const agentDetails: AgentDetails = await assistanceService.getAgentDetails(agentId);
				updateAgent(agentId, { ...agentDetails, requires_hydration: false });
			},
			[updateAgent, assistanceService],
		);

		const isCategoryLoading = useCallback(
			(category: BrowseCategory) => loadingCategories.has(category),
			[loadingCategories],
		);

		const isCategoryEmpty = useCallback(
			(category: BrowseCategory) => agentCategories[category].agentIds.length === 0,
			[agentCategories],
		);

		const hasNextPage = useCallback(
			(category: BrowseCategory) => agentCategories[category].pageInfo.hasNextPage,
			[agentCategories],
		);

		const getNextPageCursor = useCallback(
			(category: BrowseCategory) => agentCategories[category].pageInfo.endCursor,
			[agentCategories],
		);

		const abortCategoryFetch = useCallback(
			(category: BrowseCategory) => {
				setCategoryLoading(category, false);
				agentCategories[category].abortController.abort();
				agentCategories[category].abortController = new AbortController();
			},
			[agentCategories, setCategoryLoading],
		);

		const getAgentsByCategory = useCallback(
			(category: BrowseCategory) => {
				return agentCategories[category].agentIds;
			},
			[agentCategories],
		);

		return {
			agentsError,
			agentsMap,
			getAgentData,
			hydrateAgentData,
			getAgents,
			isCategoryLoading,
			hasNextPage,
			getNextPageCursor,
			abortCategoryFetch,
			getAgentsByCategory,
			isCategoryEmpty,
		};
	},
);

const noopAsync = async () => {};
const noopFn = (): Agent | undefined => undefined;
const noopArray: Agent[] = [];
const noopCategorisedAgent = {
	public: [],
	private: [],
	system: [],
};
export const noopAgent: Agent = {
	id: 'NOOP',
	name: 'NOOP',
	named_id: 'NOOP',
	user_defined_conversation_starters: [],
	description: '',
	available_plugins: {},
	actor_type: 'AGENT',
	creator_type: 'SYSTEM',
	visibility: 'PRIVATE',
	is_default: false,
	favourite: false,
	deactivated: false,
};

export const useAgentsController: typeof useAgentsControllerInternal = (param) => {
	const { isRovoEnabled } = useRovoEntitlement();
	const controllerResult = useAgentsControllerInternal(param);

	const noopUseQuery = useQuery({
		queryKey: ['noop'],
		queryFn: noopAsync,
		enabled: false,
	});

	const noopUseMutation: any = useMutation({
		mutationFn: async () => noopAgent,
		onSuccess: noopAsync,
		onError: noopAsync,
	});

	if (!isRovoEnabled) {
		return {
			availableAgents: noopArray,
			favouritedAgents: noopArray,
			categorisedAgents: noopCategorisedAgent,
			defaultAgent: undefined,
			agentsInitialized: false,
			fetchAndStoreAvailableAgents: noopUseQuery,
			deleteAgent: noopUseMutation,
			setFavouriteAgent: noopUseMutation,
			nonDefaultAgents: noopArray,
			nonDefaultFavouriteAgents: noopArray,
			updateAgentLocally: noopFn,
			agentsError: undefined,
		};
	}

	return controllerResult;
};

//Use useAgentsList in contexts in which we want to display at least three default favourited agents, such as in the editor
export const useAgentsList = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { product, experienceId, headers, touchpointSource, siteId },
		filter,
	}: UseAgentsListProps) => {
		const { availableAgents, favouritedAgents } = useAgentsController({
			assistanceServiceParams: {
				product,
				experienceId,
				headers,
				touchpointSource,
				siteId,
			},
		});

		const defaultAgents = getSuggestedOotbAgents({ availableAgents, product });

		const defaultAgentsOutsideUserFavourites = defaultAgents.filter(
			(defaultAgent) => !favouritedAgents.find((favAgent) => favAgent.id === defaultAgent.id),
		);

		const favouritedAgentsWithDefault = [
			...favouritedAgents,
			...defaultAgentsOutsideUserFavourites.slice(0, Math.max(3 - favouritedAgents.length, 0)),
		];

		return filter === 'favourites' ? favouritedAgentsWithDefault : availableAgents;
	},
);
